// contexts/AIActivationContext.jsx
import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { backend_api } from "../../static/server";

const AIActivationContext = createContext();

const CANONICAL_AI_NAMES = {
  markai: "Markai",
  fincai: "Fincai",
  operai: "Operai",
  parrai: "Parrai",
  salesai: "Salesai",
  hrrai: "Hrrai",
  complai: "Complai",
  docai: "Docai",
};

export const useAIActivation = () => {
  const context = useContext(AIActivationContext);
  if (!context) {
    throw new Error(
      "useAIActivation must be used within an AIActivationProvider",
    );
  }
  return context;
};

// Helper function to normalize AI names and bot codes to app-safe labels.
const normalizeAIName = (name) => {
  const normalizedKey = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  if (!normalizedKey) return "";

  return (
    CANONICAL_AI_NAMES[normalizedKey] ||
    `${normalizedKey.charAt(0).toUpperCase()}${normalizedKey.slice(1)}`
  );
};

const normalizeAIList = (aiNames = []) =>
  [...new Set((Array.isArray(aiNames) ? aiNames : []).map(normalizeAIName))].filter(
    Boolean,
  );

const persistActivatedAIs = (aiNames) => {
  try {
    localStorage.setItem("ActivatedAis", JSON.stringify(aiNames));
  } catch (error) {
    console.error("Error saving activated AIs:", error);
  }
};

const readStoredActivatedAIs = () => {
  try {
    const stored = localStorage.getItem("ActivatedAis");
    if (!stored) {
      return [];
    }

    try {
      const parsed = JSON.parse(stored);
      const normalized = normalizeAIList(parsed);
      persistActivatedAIs(normalized);
      return normalized;
    } catch {
      const normalized = normalizeAIList(stored.split(","));
      persistActivatedAIs(normalized);
      return normalized;
    }
  } catch (error) {
    console.error("Error loading activated AIs:", error);
    return [];
  }
};

export const AIActivationProvider = ({ children }) => {
  const { token } = useSelector((state) => state.user);
  const [activatedAIs, setActivatedAIs] = useState([]);
  const [activeAIConversations, setActiveAIConversations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const storedActivatedAIs = readStoredActivatedAIs();
    setActivatedAIs(storedActivatedAIs);

    if (!token) {
      setActivatedAIs([]);
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const syncOwnedSubscriptions = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${backend_api}ai-employees/my-subscriptions/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!isMounted) {
          return;
        }

        const ownedAIList = (res.data.results || []).map(
          (subscription) => subscription?.bot?.name || subscription?.bot?.code,
        );

        setActivatedAIs(() => {
          const nextActivated = normalizeAIList(ownedAIList);
          persistActivatedAIs(nextActivated);
          return nextActivated;
        });
      } catch (error) {
        console.error("Error syncing owned AI activations:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    syncOwnedSubscriptions();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const loadConversations = () => {
    try {
      // backend give to load conversation
      // const stored = localStorage.getItem("AIConversations");

      // if (stored) {
      //   setActiveAIConversations(JSON.parse(stored));
      // }
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const saveConversations = (conversations) => {
    try {
      // if backend have different api to save conversation of any ai 
      // localStorage.setItem("AIConversations", JSON.stringify(conversations));
    } catch (error) {
      console.error("Error saving conversations:", error);
    }
  };

  const activateAI = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    setActivatedAIs((prev) => {
      if (prev.includes(normalizedName)) {
        return prev;
      }
      const newActivated = [...prev, normalizedName];
      persistActivatedAIs(newActivated);
      return newActivated;
    });
  };

  const deactivateAI = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    setActivatedAIs((prev) => {
      const newActivated = prev.filter((name) => name !== normalizedName);
      persistActivatedAIs(newActivated);
      return newActivated;
    });
  };

  const toggleAI = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    if (activatedAIs.includes(normalizedName)) {
      deactivateAI(normalizedName);
    } else {
      activateAI(normalizedName);
    }
  };

  const isAIActivated = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    return activatedAIs.includes(normalizedName);
  };

  const saveConversation = (aiName, messages) => {
    
    const normalizedName = normalizeAIName(aiName);
    setActiveAIConversations((prev) => {
      const newConversations = {
        ...prev,
        [normalizedName]: {
          messages,
          lastUpdated: new Date().toISOString(),
        },
      };
      saveConversations(newConversations);
      return newConversations;
    });
  };

  const getConversation = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    return activeAIConversations[normalizedName]?.messages || [];
  };

  const clearConversation = (aiName) => {
    const normalizedName = normalizeAIName(aiName);
    setActiveAIConversations((prev) => {
      const newConversations = { ...prev };
      delete newConversations[normalizedName];
      saveConversations(newConversations);
      return newConversations;
    });
  };

  const value = {
    activatedAIs,
    loading,
    activateAI,
    deactivateAI,
    toggleAI,
    isAIActivated,
    saveConversation,
    getConversation,
    clearConversation,
  };

  return (
    <AIActivationContext.Provider value={value}>
      {children}
    </AIActivationContext.Provider>
  );
};
